/**
 * Avvio/viste/sessione: bootstrap, ripristino sessione, gestione viste (login/auction/setup/main),
 * logout e reset stato (estratto da FantAPP via mixin sul prototype).
 * Tutte le funzioni usano `this` (l'istanza FantAPP) esattamente come prima.
 */
import { log } from "../utils/logger.js";
import { eventManager, Events } from "../core/events.js";
import { filtersManager } from "../features/filters/index.js";
import { TableManager } from "../features/table/index.js";
import { SessionPersistence } from "../services/SessionPersistence.js";

export const bootViewMethods = {
  /**
   * Nasconde tutte le viste per evitare flicker durante bootstrap
   */
  hideAllViewsForBoot() {
    const loginScreen = document.getElementById("user-access-screen");
    const auctionView = document.getElementById("auction-view");

    if (loginScreen) {
      loginScreen.style.display = "none";
      loginScreen.hidden = true;
    }

    if (auctionView) {
      auctionView.style.display = "none";
      auctionView.hidden = true;
    }

    log.info("Bootstrap: Viste nascoste per evitare flicker");
  },

  /**
   * Carica giocatori persistiti e apre la vista asta (lista svincolati).
   */
  async _openAuctionWithPersistedPlayers({ restore, initialView, persistView = false }) {
    const players = SessionPersistence.loadPlayers();
    if (!Array.isArray(players) || players.length === 0) return false;

    await this.hydratePlayersIntoState(players);
    await this.postAuthInit();
    if (persistView) SessionPersistence.setView('auction', initialView || 'main');
    this.showAuctionView({ restore, initialView: initialView || 'main' });
    this.state.isInitialized = true;
    return true;
  },

  /**
   * Bootstrap asincrono dell'applicazione V2
   * Implementa persistenza dello stato UI con sessionStorage
   */
  async bootstrap() {
    try {
      log.info("Bootstrap: Avvio asincrono v2...");

      // Debug state iniziale
      if (window.DEBUG_MODE === 'true') {
        window.fantAppDebugger?.modules?.logger.info('[Bootstrap] Start', {
          hasSession: SessionPersistence.hasActiveSession(),
          view: SessionPersistence.getView(),
          playersMeta: SessionPersistence.getPlayersMeta()
        });
      }

      // 1. Nascondi tutte le viste per evitare flicker
      this.hideAllViewsForBoot();

      // 2. Cleanup dati corrotti
      SessionPersistence.cleanupCorruptedData();

      // 3. Inizializza servizi base
      await this.initializeBackend();
      await this.initializeServices();
      await this.initializeUI();
      this.setupEventListeners();
      this.restoreAuctionModeFromPersistence();

      // 4. LOGICA PERSISTENZA STATO UI

      // 4a. Controllo sessione
      const hasSession = SessionPersistence.hasActiveSession();
      if (!hasSession) {
        log.info("Bootstrap: Nessuna sessione, mostra login");
        SessionPersistence.setView('login');
        this.showLoginView();
        this.state.isInitialized = true;
        return;
      }

      // 4b. UserSession è già ripristinato da SessionPersistence in initializeServices()
      const sessionData = SessionPersistence.getSession();
      if (sessionData) {
        log.info("Bootstrap: Sessione attiva per", sessionData.display_name);
      }

      // 4c. Vista salvata e giocatori
      const savedView = SessionPersistence.getView();
      const hasPlayers = SessionPersistence.hasPlayers();
      const playersCount = SessionPersistence.getPlayersMeta()?.count || 0;

      log.info("Bootstrap: Stato persistente -", {
        view: savedView?.name,
        subView: savedView?.subView,
        hasPlayers,
        playersCount
      });

      if (savedView?.name === 'auction' && hasPlayers) {
        log.info("Bootstrap: Ripristino vista auction con giocatori");
        const restored = await this._openAuctionWithPersistedPlayers({
          restore: true,
          initialView: savedView.subView || 'main',
        });
        if (restored) {
          log.info("Bootstrap: Vista auction ripristinata con successo");
          return;
        }
      }

      if (hasPlayers) {
        log.info("Bootstrap: Giocatori disponibili, vai alla main interface");
        const restored = await this._openAuctionWithPersistedPlayers({
          restore: false,
          initialView: 'main',
          persistView: true,
        });
        if (restored) return;
      }

      log.info("Bootstrap: Vai a setup screen");
      SessionPersistence.setView('setup');
      await this.postAuthInit();
      this.showAuctionView(); // Mostrerà la setup screen CON caricamento automatico

      this.state.isInitialized = true;
      log.info("Bootstrap: Completato con successo");

    } catch (error) {
      log.error("Bootstrap: Errore critico", error);
      // In caso di errore, fallback sicuro
      SessionPersistence.setView('login');
      this.showLoginView();
      this.state.isInitialized = true;
    }
  },

  /**
   * Ripristina stato giocatori da sessionStorage nel store UI.
   * Ricarica sempre le stats stagione 25/26 da PocketBase (la sessione può avere 0 dalle quotazioni).
   */
  async hydratePlayersIntoState(players) {
    try {
      log.info("Hydration: Ripristino", players.length, "giocatori da sessionStorage");

      if (!Array.isArray(players) || players.length === 0) {
        log.warn("Hydration: Nessun giocatore da ripristinare");
        return;
      }

      // *** FIX: Applica splitting ruoli durante ripristino ***
      let playersWithSplitRoles = players.map(player => {
        if (player.role && (!player.roles || player.roles.length === 0)) {
          // Se non ha ruoli splittati, applicare splitting
          const roles = player.role.split(/[,\/;]/).map(r => r.trim()).filter(Boolean);
          player.roles = roles.length > 0 ? roles : [player.role];
          console.log(`[HYDRATION] Split ruoli per ${player.name}: '${player.role}' -> [${player.roles.join(', ')}]`);
        }
        return player;
      });

      // Overlay stats 25/26 (altrimenti restano gli 0 delle quotazioni in sessionStorage)
      if (typeof this.hydratePlayersWithStats === "function") {
        try {
          playersWithSplitRoles = await this.hydratePlayersWithStats(playersWithSplitRoles);
          SessionPersistence.savePlayers(playersWithSplitRoles);
        } catch (err) {
          log.warn("Hydration: stats stagione non ricaricate, continuo", err);
        }
      }

      // Aggiorna stato principale
      this.state.allPlayers = playersWithSplitRoles;
      this.state.playersHydrated = playersWithSplitRoles; // Per compatibilità test

      // *** FIX: Usa SOLO il flusso FiltersManager → Events.TABLE_UPDATE → TableManager ***
      // Aggiorna filtri manager (che poi emettera' Events.TABLE_UPDATE per il TableManager)
      if (filtersManager) {
        filtersManager.setPlayers(playersWithSplitRoles);
        // Non applicare filtri qui: i target Studio si caricano in reloadUserData()
        // I filtri verranno applicati in reloadUserData() o showMainInterface()
        console.log('[HYDRATION] 📊 Dati iniettati nel FiltersManager per ripristino');
      }

      // *** RIMOSSO: Chiamata diretta updatePlayers bypasserebbe i filtri ***
      // Il TableManager riceverà i dati tramite Events.TABLE_UPDATE quando i filtri verranno applicati

      log.info("Hydration: Stato ripristinato con", playersWithSplitRoles.length, "giocatori");

    } catch (error) {
      log.error("Hydration: Errore ripristino stato", error);
      // Non bloccare l'app, continua senza dati
    }
  },

  /**
   * Inizializzazione post-autenticazione
   */
  async postAuthInit() {
    try {
      // Setup logout
      this.setupLogout();

      // Eventuali altri setup post-auth
      log.info("PostAuth: Inizializzazione completata");

    } catch (error) {
      log.warn("PostAuth: Errore inizializzazione", error);
    }
  },

  // ======== PERSISTENZA SESSIONE ========

  /**
   * Mostra la schermata di login
   */
  showLoginView() {
    console.log('[BOOTSTRAP] 🔑 showLoginView chiamato');

    // Nasconde tutte le altre viste
    const mainView = document.getElementById("auction-view");
    if (mainView) {
      mainView.hidden = true;
      mainView.style.display = "none";
      console.log('[BOOTSTRAP] ✅ auction-view nascosto');
    }

    // Mostra la schermata di accesso
    const loginScreen = document.getElementById("user-access-screen");
    if (loginScreen) {
      loginScreen.style.display = "block";
      loginScreen.hidden = false;
      console.log('[BOOTSTRAP] ✅ user-access-screen mostrato');
      console.log('[BOOTSTRAP] 📏 Dimensioni:', loginScreen.offsetWidth, 'x', loginScreen.offsetHeight);
      console.log('[BOOTSTRAP] 📄 Contenuto:', loginScreen.innerHTML.length > 0 ? 'Presente' : 'Vuoto');
    } else {
      console.error('[BOOTSTRAP] ❌ user-access-screen NON TROVATO!');
    }

    // Assicurati che la schermata di accesso sia pulita e inizializzata
    if (this.userAccessScreen) {
      console.log('[BOOTSTRAP] 📱 userAccessScreen esistente, chiamo reset/show');
      if (this.userAccessScreen.reset) {
        this.userAccessScreen.reset(); // Pulisce SEMPRE il form
      }
      if (this.userAccessScreen.show) {
        this.userAccessScreen.show();
      }
    } else {
      console.error('[BOOTSTRAP] ❌ userAccessScreen NON INIZIALIZZATO!');
    }

    this.state.currentView = "user-access";
    console.log('[BOOTSTRAP] ✅ Vista impostata su:', this.state.currentView);
    log.info("Vista: Schermata di login mostrata e pulita");
  },

  /**
   * Mostra la vista principale dell'asta con persistenza dello stato V2
   */
  async showAuctionView({ restore = false, initialView = 'auto' } = {}) {
    console.log('[AUCTION_VIEW] 🎯 showAuctionView chiamato', { restore, initialView });

    // Nascondi login
    const loginScreen = document.getElementById("user-access-screen");
    if (loginScreen) {
      loginScreen.hidden = true;
      loginScreen.style.display = "none";
      console.log('[AUCTION_VIEW] ✅ Login screen nascosto');
    }

    // Mostra vista asta
    const auctionView = document.getElementById("auction-view");
    if (auctionView) {
      auctionView.hidden = false;
      auctionView.style.display = "block";
      console.log('[AUCTION_VIEW] ✅ Auction view mostrato');
    }

    // DECISIONE: Setup o Main Interface, basata su giocatori caricati E asta attiva
    const hasPlayers = this.state.allPlayers && this.state.allPlayers.length > 0;
    let hasActiveAuction = !!this.state.activeAuctionId;
    let auctionScreenAlreadyShown = false;
    if (!hasActiveAuction && typeof this.ensureActiveAuction === 'function') {
      // ensureActiveAuction mostra da sola la schermata unica se non trova un'asta valida
      hasActiveAuction = await this.ensureActiveAuction();
      auctionScreenAlreadyShown = !hasActiveAuction;
    }
    console.log('[AUCTION_VIEW] 🎮 Giocatori presenti:', hasPlayers ? this.state.allPlayers.length : 0, '- asta attiva:', hasActiveAuction);

    let targetView = initialView;
    if (initialView === 'auto') {
      targetView = (hasPlayers && hasActiveAuction) ? 'main' : 'setup';
      console.log('  - auto-detected targetView:', targetView);
    }

    // PERSISTENZA VISTA IMMEDIATA (solo se non in modalità restore)
    if (!restore) {
      console.log('[AUCTION_VIEW] 💾 Salvataggio vista:', targetView);
      SessionPersistence.setView('auction', targetView);
    } else {
      console.log('[AUCTION_VIEW] 🔄 Modalità restore, mantengo vista salvata');
    }

    if (hasPlayers && hasActiveAuction && targetView === 'main') {
      // Tutto pronto -> mostra interfaccia principale
      console.log('[AUCTION_VIEW] 📈 Mostra main interface');
      await this.showMainInterface();

      if (!restore) {
        SessionPersistence.savePlayers(this.state.allPlayers);
        console.log('[AUCTION_VIEW] 💾 Giocatori salvati in sessione');
      }
    } else if (!auctionScreenAlreadyShown) {
      // Manca la lista giocatori e/o l'asta -> mostra la schermata unica
      console.log('[AUCTION_VIEW] ⚙️ Mostra schermata unica (lista + asta)');
      this.showUnifiedSetupScreen();
    }

    // Setup comuni
    this.setupLogout();

    this.state.currentView = "auction";
    this.updateUserHeader();

    log.info("Vista: Auction view attiva -", { targetView, hasPlayers, hasActiveAuction, restore });
  },

  /**
   * Mostra l'interfaccia principale (lista svincolati)
   */
  async showMainInterface() {
    console.log('[AUCTION_VIEW] 📄 Mostra main interface');
    console.log('[AUCTION_VIEW] 📈 Dati disponibili:', this.state.allPlayers.length, 'giocatori');

    const setupScreen = document.getElementById("auction-setup-screen");
    const mainInterface = document.getElementById("auction-main-interface");

    if (setupScreen) {
      setupScreen.hidden = true;
      setupScreen.style.display = "none";
      console.log('[AUCTION_VIEW] ➖ Setup screen nascosto');
    }

    if (mainInterface) {
      mainInterface.hidden = false;
      mainInterface.style.display = "block";
      console.log('[AUCTION_VIEW] ✅ Main interface mostrato');
    } else {
      console.error('[AUCTION_VIEW] ❌ Main interface NON TROVATO!');
    }

    // Inizializza table manager se necessario
    if (!this.tableManager) {
      this.tableManager = new TableManager(".players-table");

      // Setup controlli paginazione
      this.setupPaginationControls();
    }

    // Inizializza tabella se ci sono giocatori
    if (this.state.allPlayers.length > 0) {
      console.log('[AUCTION_VIEW] 📉 Inizializzazione tabella auction...');

      // *** FIX: Carica dati utente PRIMA di inizializzare la tabella ***
      try {
        await this.reloadUserData();
        console.log('[AUCTION_VIEW] ✅ Dati utente caricati prima della tabella');
      } catch (err) {
        log.warn('Errore caricamento dati utente:', err);
      }

      // *** FIX CRITICO: Usa SOLO il flusso FiltersManager → Events.TABLE_UPDATE → TableManager ***
      // Non chiamare mai tableManager.updatePlayers() direttamente - bypasserebbe i filtri!
      // I dati vengono iniettati nel FiltersManager e poi emessi via Events.TABLE_UPDATE
      filtersManager.setPlayers(this.state.allPlayers);
      console.log('[AUCTION_VIEW] 📊 Dati iniettati nel FiltersManager:', this.state.allPlayers.length, 'giocatori');

      this.initializeAuctionTable();

      console.log('[AUCTION_VIEW] ✅ Tabella inizializzata e dati caricati');
    } else {
      console.warn('[AUCTION_VIEW] ⚠️ Nessun giocatore da mostrare');
    }

    this.setupAppShell?.();
    this.syncAppShellNav?.();
    this.syncAppShellCounts?.();

    log.info("Vista: Main interface attiva");
  },

  /**
   * Setup del pulsante logout e gestione chiusura tab
   */
  setupLogout() {
    // Trova il pulsante logout
    const logoutBtn = document.querySelector('#logout-btn, .logout-btn, [data-action="logout"]');
    if (logoutBtn) {
      // Rimuovi listener esistenti per evitare duplicati
      logoutBtn.replaceWith(logoutBtn.cloneNode(true));
      const newLogoutBtn = document.querySelector('#logout-btn, .logout-btn, [data-action="logout"]');

      newLogoutBtn.addEventListener('click', () => {
        log.info("Logout: Richiesto dall'utente");
        this.handleLogout();
      });

      log.info("Logout: Event listener configurato");
    } else {
      log.warn("Logout: Pulsante non trovato nel DOM");
    }

    // NON aggiungere beforeunload listener - sessionStorage persiste automaticamente sui refresh
    // e si cancella alla chiusura del browser/tab (comportamento nativo desiderato)
  },

  /**
   * Gestisce il logout dell'utente con persistenza V2
   */
  handleLogout() {
    try {
      log.info("Logout: Avvio procedura logout");

      // *** V2: Cancella SessionPersistence ***
      SessionPersistence.clearSession();

      // Cancella anche la sessione legacy
      if (this.state.userSession) {
        this.state.userSession.logout();
      }

      // Reset dello stato dell'app
      this.resetAppState();

      // Mostra schermata login
      this.showLoginView();

      log.info("Logout: Completato con successo");

      // Messaggio di conferma
      if (window.showToast) {
        window.showToast("Logout effettuato con successo", "success");
      }
    } catch (error) {
      log.error("Logout: Errore durante logout", error);

      // Fallback: forza pulizia e redirect
      try {
        SessionPersistence.clearSession();
        this.showLoginView();
      } catch (fallbackError) {
        log.error("Logout: Errore fallback", fallbackError);
        // Ultima risorsa: reload pagina
        window.location.reload();
      }
    }
  },

  /**
   * Reset dello stato dell'applicazione
   */
  resetAppState() {
    // Reset dati giocatori
    this.state.allPlayers = [];
    this.state.playersHydrated = [];
    this.state.studioTargets.clear();
    this.state.studioUserLabels = [];
    this.state.studioUserLabelColors = {};
    if (this.state.studioDrafts instanceof Map) this.state.studioDrafts.clear();
    else this.state.studioDrafts = new Map();

    // Reset filtri
    if (filtersManager) {
      filtersManager.reset();
    }

    // Reset table manager
    if (this.tableManager) {
      this.tableManager.clear();
      this.tableManager = null;
    }

    log.info("App: Stato resettato");
  },
};
